'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { clsx } from 'clsx';
import { sendGAEvent } from '@next/third-parties/google';
import { autoGrowTextarea } from '@/lib/autoGrow';
import Button from './Button';

type FormData = {
  propertyType: string;
  size: string;
  startTime: string;
  name: string;
  phone: string;
  comment: string;
  messenger: 'Telegram' | 'WhatsApp' | 'Phone call';
};

const TOTAL_STEPS = 5;

// GA funnel labels for each wizard step (index 0 = step 1).
const STEP_NAMES = ['property_type', 'size', 'start_time', 'name', 'phone'] as const;

const iconProps = {
  width: 28,
  height: 28,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

// New build — a modern multi-section apartment complex.
function NewBuildIcon() {
  return (
    <svg {...iconProps}>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </svg>
  );
}

// Secondary market — an existing apartment block.
function ApartmentIcon() {
  return (
    <svg {...iconProps}>
      <rect width="16" height="20" x="4" y="2" rx="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" />
    </svg>
  );
}

// House — detached home.
function HouseIcon() {
  return (
    <svg {...iconProps}>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </svg>
  );
}

export default function MultiStepForm() {
  const t = useTranslations('form');
  const locale = useLocale();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  // Honeypot kept OUT of react-hook-form so it never appears in validated data.
  const [honeypot, setHoneypot] = useState('');
  // Timestamp the form was first rendered — used by the server's timing check.
  const startedAtRef = useRef(Date.now());
  // Fires the GA `form_start` event once, on the first step advance.
  const formStartedRef = useRef(false);

  const nameRef = useRef<HTMLInputElement | null>(null);

  // Focus the name field when its step opens — desktop (fine pointer) only, so
  // touch devices don't pop the on-screen keyboard. preventScroll avoids a jump.
  useEffect(() => {
    if (step === 4 && window.matchMedia('(pointer: fine)').matches) {
      nameRef.current?.focus({ preventScroll: true });
    }
  }, [step]);

  // Built here so validation messages are translated per locale.
  const schema = useMemo(
    () =>
      z.object({
        propertyType: z.string().min(1, t('errorRequired')),
        size: z.string().min(1, t('errorRequired')),
        startTime: z.string().min(1, t('errorRequired')),
        name: z
          .string()
          .trim()
          .min(1, t('errorInputRequired'))
          .max(191, t('errorNameTooLong')),
        phone: z
          .string()
          .trim()
          .min(1, t('errorInputRequired'))
          .max(20, t('errorPhoneTooLong'))
          .regex(/^[0-9\s\+\-\(\)]+$/, t('errorPhoneFormat'))
          .refine((val) => {
            if (!val) return true;
            const digitCount = (val.match(/\d/g) || []).length;
            return digitCount >= 9 && digitCount <= 15;
          }, t('errorPhoneFormat')),
        comment: z.string().max(191, t('errorCommentTooLong')).optional(),
        // Must be in the schema or zodResolver strips it from the submitted
        // values (it has a default, so it never shows a validation error).
        messenger: z.enum(['Telegram', 'WhatsApp', 'Phone call']),
      }),
    [t]
  );

  const { register, handleSubmit, watch, setValue, trigger, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    // onChange keeps the errors object fresh so a shown error clears the moment
    // the field is fixed. Errors stay hidden until a Next/Submit attempt via the
    // `showErrors` gate, so validation never fires prematurely while typing.
    mode: 'onChange',
    defaultValues: { messenger: 'WhatsApp', propertyType: 'new', size: 'under50', startTime: 'asap', comment: '' },
  });

  const propertyType = watch('propertyType');
  const size = watch('size');
  const startTime = watch('startTime');
  const messenger = watch('messenger');
  const comment = watch('comment') ?? '';

  // Register the name field once and merge RHF's ref with ours so we can focus
  // it when step 4 opens (see the effect above).
  const { ref: nameFieldRef, ...nameField } = register('name');

  async function next() {
    setShowErrors(true);
    // GA: first progression through the wizard = the user started the form.
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      sendGAEvent('event', 'form_start', { form_type: 'calculator' });
    }
    const fields: (keyof FormData)[] = ['propertyType', 'size', 'startTime', 'name', 'phone'];
    const valid = await trigger(fields[step - 1]);
    if (valid) {
      // GA funnel: the user completed `step` and is advancing. Lets you see
      // drop-off across the steps (step 5 completion = the generate_lead below).
      sendGAEvent('event', 'form_step', {
        form_type: 'calculator',
        step,
        step_name: STEP_NAMES[step - 1],
      });
      setStep((s) => Math.min(s + 1, TOTAL_STEPS));
      setShowErrors(false);
    }
  }

  function prev() {
    setStep((s) => Math.max(s - 1, 1));
  }

  const onSubmit = async (values: FormData) => {
    setShowErrors(true);
    setSubmitError(false);
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'estimate',
          propertyType: values.propertyType,
          size: values.size,
          startTime: values.startTime,
          name: values.name,
          phone: values.phone,
          comment: values.comment || '',
          messenger: values.messenger,
          locale,
          company: honeypot,
          startedAt: startedAtRef.current,
        }),
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.ok) {
        setSubmitted(true);
        // GA conversion: a calculator (estimate) lead was captured. No PII —
        // only the form type, chosen messenger, and locale.
        sendGAEvent('event', 'generate_lead', {
          form_type: 'calculator',
          messenger: values.messenger,
          locale,
        });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-16 space-y-4">
        <div className="w-20 h-20 rounded-full bg-primary-container flex items-center justify-center mx-auto">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 className="font-headline text-headline-lg text-on-surface">{t('successTitle')}</h3>
        <p className="text-on-surface-variant text-body-lg">{t('successDesc')}</p>
      </div>
    );
  }

  const propertyOptions = [
    { value: 'new', label: t('step1Option1'), Icon: NewBuildIcon },
    { value: 'secondary', label: t('step1Option2'), Icon: ApartmentIcon },
    { value: 'house', label: t('step1Option3'), Icon: HouseIcon },
  ];

  const sizeOptions = [
    { value: 'under50', label: t('step2Option1') },
    { value: '50-70', label: t('step2Option2') },
    { value: '70-90', label: t('step2Option3') },
    { value: '90-120', label: t('step2Option4') },
    { value: '120+', label: t('step2Option5') },
  ];

  const timeOptions = [
    { value: 'asap', label: t('step3Option1') },
    { value: 'this-month', label: t('step3Option2') },
    { value: 'next-month', label: t('step3Option3') },
    { value: '2-3-months', label: t('step3Option4') },
    { value: '3+months', label: t('step3Option5') },
    { value: 'other', label: t('step3Option6') },
  ];

  // All steps share one grid cell, so the card stays a fixed height (the tallest
  // step) and never resizes between steps. The bulky steps are compacted on
  // mobile (below) so that fixed height still fits the screen above the dock.
  // Only the active step is visible; `invisible` also drops the rest from tab +
  // screen-reader order. Opacity cross-fades the active step in.
  const stepClass = (n: number) =>
    clsx(
      'col-start-1 row-start-1 transition-opacity duration-200 motion-reduce:transition-none',
      step === n ? 'visible opacity-100' : 'invisible opacity-0'
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Lock every control while the request is in flight: disables the option
          buttons, inputs, messenger pills, and the Back button — so nothing can
          change, and the user can't step back, mid-submit. */}
      <fieldset disabled={isSubmitting} className="m-0 min-w-0 space-y-5 border-0 p-0 sm:space-y-8">
      {/* Honeypot: hidden from humans, irresistible to bots. A filled value
          makes the server drop the submission silently. */}
      <input
        type="text"
        name="company"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />
      {/* Progress bar */}
      <div className="space-y-2">
        <p className="text-label-caps font-headline text-outline uppercase tracking-widest">
          {t('stepOf', { current: step, total: TOTAL_STEPS })}
        </p>
        <div className="h-1 bg-surface-container-high rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500 rounded-full"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      {/* Steps share one grid cell → fixed card height (the tallest step), no
          resizing between steps. Bulky steps are compacted on mobile so it still
          fits the screen. Reserved error rows keep step heights constant. */}
      <div className="grid grid-cols-1">
        {/* Step 1: Property type */}
        <div className={stepClass(1)}>
          <div className="space-y-4">
            <h3 className="font-headline text-headline-md text-on-surface">{t('step1Label')}</h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
              {propertyOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setValue('propertyType', opt.value, { shouldValidate: true })}
                  className={clsx(
                    'flex items-center gap-3 rounded-lg border p-4 text-left transition-all duration-200 sm:flex-col sm:items-start sm:gap-3 sm:p-6',
                    propertyType === opt.value
                      ? 'border-primary bg-primary-container text-on-surface'
                      : 'border-outline-variant bg-surface-container text-on-surface-variant hover:border-primary/50'
                  )}
                >
                  <span className={clsx('block shrink-0', propertyType === opt.value ? 'text-primary' : 'text-on-surface-variant')}>
                    <opt.Icon />
                  </span>
                  <p className="font-headline font-semibold">{opt.label}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Step 2: Size */}
        <div className={stepClass(2)}>
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-headline text-headline-md text-on-surface">{t('step2Label')}</h3>
              {showErrors && errors.size && <p className="text-error text-xs">{errors.size.message}</p>}
            </div>
            <div className="grid auto-rows-fr grid-cols-2 sm:grid-cols-3 gap-3">
              {sizeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setValue('size', opt.value, { shouldValidate: true })}
                  className={clsx(
                    'flex items-center justify-center p-4 rounded-lg border text-center transition-all duration-200',
                    size === opt.value
                      ? 'border-primary bg-primary-container text-on-surface'
                      : 'border-outline-variant bg-surface-container text-on-surface-variant hover:border-primary/50'
                  )}
                >
                  <span className="min-w-0 break-words font-headline font-semibold">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Step 3: Start time */}
        <div className={stepClass(3)}>
          <div className="space-y-4">
            <h3 className="font-headline text-headline-md text-on-surface">{t('step3Label')}</h3>
            <div className="grid auto-rows-fr grid-cols-2 gap-2 sm:gap-3">
              {timeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setValue('startTime', opt.value, { shouldValidate: true })}
                  className={clsx(
                    'flex items-center px-2 py-3 rounded-lg border text-left text-sm leading-tight transition-all duration-200 sm:px-4 sm:py-4 sm:text-base sm:leading-normal',
                    startTime === opt.value
                      ? 'border-primary bg-primary-container text-on-surface'
                      : 'border-outline-variant bg-surface-container text-on-surface-variant hover:border-primary/50'
                  )}
                >
                  <span className="min-w-0 break-words font-headline font-semibold">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Step 4: Name */}
        <div className={stepClass(4)}>
          <div className="space-y-4">
            <h3 className="font-headline text-headline-md text-on-surface">{t('step4Label')}</h3>
            <input
              {...nameField}
              ref={(el) => {
                nameFieldRef(el);
                nameRef.current = el;
              }}
              placeholder={t('step4Placeholder')}
              className="w-full bg-transparent border-b border-outline-variant focus:border-primary outline-none text-on-surface py-4 text-body-lg placeholder:text-outline transition-colors"
            />
            {/* Reserved error row — always rendered so the step height is stable */}
            <p className="text-error text-xs min-h-[1rem]">{showErrors && errors.name ? errors.name.message : ' '}</p>

            {/* Optional: a short description of the job. Capped at 191 chars with a
                live counter; never required. */}
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-label-caps font-headline text-outline uppercase tracking-widest">
                  {t('commentLabel')}
                </span>
                <span className="text-xs text-outline">{t('commentOptional')}</span>
              </div>
              <div className="overflow-hidden rounded-lg border border-outline-variant bg-surface-container transition-colors focus-within:border-primary">
                <textarea
                  {...register('comment')}
                  maxLength={191}
                  rows={3}
                  onInput={autoGrowTextarea}
                  placeholder={t('commentPlaceholder')}
                  className="block w-full resize-none overflow-hidden bg-transparent px-4 py-3 text-body-md text-on-surface outline-none placeholder:text-outline"
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                {showErrors && errors.comment ? (
                  <p className="text-error text-xs">{errors.comment.message}</p>
                ) : (
                  <span />
                )}
                <span className={clsx('text-xs tabular-nums', comment.length >= 171 ? 'text-primary' : 'text-outline')}>
                  {comment.length}/191
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 5: Phone */}
        <div className={stepClass(5)}>
          <div className="space-y-4">
            <h3 className="font-headline text-headline-md text-on-surface">{t('step5Label')}</h3>
            <input
              {...register('phone')}
              type="tel"
              placeholder={t('step5Placeholder')}
              className="w-full bg-transparent border-b border-outline-variant focus:border-primary outline-none text-on-surface py-4 text-body-lg placeholder:text-outline transition-colors"
            />
            {/* Reserved error row — always rendered so the step height is stable */}
            <p className="text-error text-xs mt-2 min-h-[1rem]">{showErrors && errors.phone ? errors.phone.message : ' '}</p>
            <div className="space-y-3">
              <span className="text-label-caps font-headline text-outline uppercase tracking-widest">
                {t('step5ContactVia')}
              </span>
              <div className="grid grid-cols-3 gap-2">
                {/* Telegram/WhatsApp are brand names (identical in every locale);
                    only the phone-call option is translated. The submitted value
                    stays canonical English so the company's inbox is consistent. */}
                {([
                  { value: 'Telegram', label: 'Telegram' },
                  { value: 'WhatsApp', label: 'WhatsApp' },
                  { value: 'Phone call', label: t('messengerPhoneCall') },
                ] as const).map(({ value, label }) => (
                  <label key={value} className="cursor-pointer">
                    <input type="radio" {...register('messenger')} value={value} className="sr-only peer" />
                    <span className={clsx(
                      'block px-2 py-3 rounded-full border text-center text-label-caps font-headline transition-all sm:px-4',
                      messenger === value
                        ? 'bg-primary border-primary text-background font-bold'
                        : 'border-outline-variant text-on-surface-variant hover:border-primary/50'
                    )}>
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {submitError && (
        <p className="text-error text-sm font-medium text-center">{t('submitError')}</p>
      )}

      {/* Navigation — pt-2 adds a clear gap from the step's last field so the
          action buttons never crowd the messenger pills on step 5. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 pt-2">
        {step > 1 && (
          <Button
            type="button"
            variant="secondary"
            onClick={prev}
          >
            {t('prev')}
          </Button>
        )}
        {step < TOTAL_STEPS ? (
          <Button
            type="button"
            onClick={next}
            fullWidth={step === 1}
            className={clsx('cta-gold-hover !bg-[#F5F0E8] min-h-14 sm:min-h-0', step > 1 && 'flex-1')}
          >
            {t('next')}
          </Button>
        ) : (
          <Button
            type="submit"
            arrow
            fullWidth={step === 1}
            className={clsx('cta-gold-hover !bg-[#F5F0E8] min-h-14 sm:min-h-0', step > 1 && 'flex-1')}
            onClick={() => setShowErrors(true)}
            loading={isSubmitting}
          >
            {isSubmitting ? t('sending') : t('submitCta')}
          </Button>
        )}
      </div>
      </fieldset>
    </form>
  );
}
